import { useEffect, useState, type ReactNode } from 'react';
import type { FormField } from '../surveys/types';
import { useFormulaEngine } from './useFormulaEngine';

/**
 * Renderer auxiliar para los nuevos tipos de campo del motor extendido
 * (calculated, lookup, reference). Pensado para integrarse dentro de
 * `PublicSurveyForm` (o cualquier renderer existente) tomando los campos
 * que no son nativos y delegando aquí.
 *
 * No reemplaza al renderer existente; lo complementa. La integración
 * mínima esperada en `PublicSurveyForm` es:
 *
 *   const engine = useFormulaEngine({ fields, values });
 *   ... y al renderizar cada field, si el tipo es calculated/lookup/reference
 *   delegar a <ExtendedField field={f} value={values[f.id]} engine={engine} ... />.
 */

interface ExtendedFieldProps {
  field: FormField;
  value: unknown;
  onChange: (value: unknown) => void;
  engine: ReturnType<typeof useFormulaEngine>;
  disabled?: boolean;
}

export function ExtendedField({ field, value, onChange, engine, disabled }: ExtendedFieldProps) {
  if (field.type === 'calculated') return <CalculatedField field={field} engine={engine} />;
  if (field.type === 'lookup' || field.type === 'reference') {
    return <LookupField field={field} value={value} onChange={onChange} engine={engine} disabled={disabled} />;
  }
  return <StandardField field={field} value={value} onChange={onChange} disabled={disabled} />;
}

function FieldShell({
  field,
  children,
  htmlFor,
}: {
  field: FormField;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className='rounded-lg border border-border bg-card px-5 py-4 shadow-sm'>
      <label htmlFor={htmlFor} className='mb-2 block text-sm font-medium text-foreground'>
        {field.label}
        {field.required && <span className='ml-1 text-destructive'>*</span>}
      </label>
      {children}
    </div>
  );
}

function asString(value: unknown): string {
  return value === undefined || value === null ? '' : String(value);
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

function updateMultiValue(current: unknown, option: string, checked: boolean): string[] {
  const values = asStringArray(current);
  if (checked) return values.includes(option) ? values : [...values, option];
  return values.filter(value => value !== option);
}

function StandardField({ field, value, onChange, disabled }: Omit<ExtendedFieldProps, 'engine'>) {
  if (field.type === 'textarea') {
    return (
      <FieldShell field={field} htmlFor={field.id}>
        <textarea
          id={field.id}
          className='min-h-28 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20'
          placeholder={field.placeholder}
          value={asString(value)}
          onChange={event => onChange(event.target.value)}
          disabled={disabled}
        />
      </FieldShell>
    );
  }

  if (field.type === 'select') {
    return (
      <FieldShell field={field} htmlFor={field.id}>
        <select
          id={field.id}
          className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20'
          value={asString(value)}
          onChange={event => onChange(event.target.value)}
          disabled={disabled}
        >
          <option value=''>Seleccionar…</option>
          {(field.options ?? []).map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </FieldShell>
    );
  }

  if (field.type === 'radio') {
    return (
      <FieldShell field={field}>
        <div className='space-y-2'>
          {(field.options ?? []).map(option => (
            <label key={option} className='flex items-center gap-2 text-sm text-foreground'>
              <input
                type='radio'
                name={field.id}
                value={option}
                checked={value === option}
                onChange={() => onChange(option)}
                disabled={disabled}
                className='border-input text-primary'
              />
              {option}
            </label>
          ))}
        </div>
      </FieldShell>
    );
  }

  if (field.type === 'checkbox-multiple') {
    return (
      <FieldShell field={field}>
        <div className='space-y-2'>
          {(field.options ?? []).map(option => (
            <label key={option} className='flex items-center gap-2 text-sm text-foreground'>
              <input
                type='checkbox'
                checked={asStringArray(value).includes(option)}
                onChange={event => onChange(updateMultiValue(value, option, event.target.checked))}
                disabled={disabled}
                className='rounded border-input text-primary'
              />
              {option}
            </label>
          ))}
        </div>
      </FieldShell>
    );
  }

  if (field.type === 'checkbox') {
    return (
      <FieldShell field={field}>
        <label className='flex items-center gap-2 text-sm text-foreground'>
          <input
            type='checkbox'
            checked={value === true}
            onChange={event => onChange(event.target.checked)}
            disabled={disabled}
            className='rounded border-input text-primary'
          />
          Sí
        </label>
      </FieldShell>
    );
  }

  if (field.type === 'attachment' || field.type === 'signature') {
    return (
      <FieldShell field={field}>
        <p className='text-sm text-muted-foreground'>Este tipo de campo todavía no está disponible en capturas de libros.</p>
      </FieldShell>
    );
  }

  return (
      <FieldShell field={field} htmlFor={field.id}>
      <input
        id={field.id}
        type={field.type === 'number' || field.type === 'date' ? field.type : 'text'}
        className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20'
        placeholder={field.placeholder}
        min={field.validation?.min}
        max={field.validation?.max}
        value={asString(value)}
        onChange={event => {
          const next = field.type === 'number' && event.target.value !== ''
            ? event.target.valueAsNumber
            : event.target.value;
          onChange(next);
        }}
        disabled={disabled}
      />
    </FieldShell>
  );
}

function CalculatedField({
  field,
  engine,
}: { field: FormField; engine: ReturnType<typeof useFormulaEngine> }) {
  const value = engine.calculated[field.id];
  const display = value === null || value === undefined ? '—' : String(value);
  return (
    <div className='space-y-1'>
      <label className='block text-sm font-medium text-foreground'>
        {field.label}
        <span className='ml-2 text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary'>calculado</span>
      </label>
      <div
        className='px-3 py-2 rounded-md border border-border bg-muted text-sm text-foreground'
        data-testid={`calculated-${field.id}`}
      >
        {display}
      </div>
    </div>
  );
}

interface LookupFieldProps extends ExtendedFieldProps {
  field: FormField;
  engine: ReturnType<typeof useFormulaEngine>;
}

function LookupField({ field, value, onChange, engine, disabled }: LookupFieldProps) {
  const src = field.source;
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!src) return;
    const handle = setTimeout(() => {
      void engine.loadLookup(src.templateCode, src.valueField, src.displayField, search || undefined);
    }, 250);
    return () => clearTimeout(handle);
  }, [search, src, engine]);

  if (!src) {
    return (
      <p className='text-xs text-destructive'>
        Campo {field.label} sin `source` configurado.
      </p>
    );
  }

  const options = engine.getLookupOptions(src.templateCode, src.valueField, src.displayField);

  return (
    <div className='space-y-1'>
      <label className='block text-sm font-medium text-foreground'>
        {field.label}
      </label>
      <input
        type='text'
        className='block w-full px-3 py-2 border border-input rounded-md bg-background text-sm'
        placeholder='Buscar…'
        value={search}
        onChange={(e) => setSearch(e.target.value.slice(0, 128))}
        disabled={disabled}
      />
      <select
        className='block w-full px-3 py-2 border border-input rounded-md bg-background text-sm'
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        <option value=''>— Seleccionar —</option>
        {options.map((opt) => (
          <option key={opt.responseId} value={String(opt.value)}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}
